import { useEffect, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import { FronteggProvider, FronteggThemeOptions, useAuth } from '@frontegg/react';
import { ContextHolder } from '@frontegg/rest-api';
import AuthService from './services/auth.service';

const authService = AuthService.getInstance();

const contextOptions = {
  baseUrl: authService.authUrl,
  clientId: authService.feClientId,
  appId: authService.feAppId,
};

const authOptions = {
  keepSessionAlive: true,
  routes: {
    loginUrl: (authService.useExternalLogin || authService.isCustomDomain) ? '/ext-login' : '/login',
    logoutUrl: '/do/logout',
    authenticatedUrl: '/',
  },
};

const themeOptions: FronteggThemeOptions = {
  loginBox: {},
};

// Custom onRedirectTo that intercepts OAuth authorize attempts
function ssoRedirectInterceptor(path: string, opts?: any) {
  if (path.includes('/oauth/authorize')) {
    if (authService.isCustomDomain && authService.ssoLoginUrl && !document.cookie.includes('plb_sso_attempted')) {
      document.cookie = 'plb_sso_attempted=1; max-age=300; secure; samesite=lax; path=/';
      const returnTo = encodeURIComponent(window.location.origin);
      const returnToken = encodeURIComponent(authService.returnToken || '');
      const idpUrl = encodeURIComponent(authService.ssoLoginUrl!);
      // Redirect through Flask sso-bounce endpoint which stores returnTo in
      // the server session, then 302s to the IdP.  This avoids localStorage
      // timing issues with React/Frontegg.
      window.location.href = `${authService.authAppUrl}/api/v1/env/sso-bounce?idpUrl=${idpUrl}&returnTo=${returnTo}&returnToken=${returnToken}`;
    } else if (authService.isCustomDomain) {
      window.location.href = '/ext-login';
    } else {
      window.location.href = '/login';
    }
    return;
  }
  // Let all other Frontegg redirects through normally
  if (opts?.refresh) {
    window.location.href = path;
  } else if (opts?.replace) {
    window.history.replaceState(null, '', path);
  } else {
    window.history.pushState(null, '', path);
  }
}

// Set it before FronteggProvider (catches early redirects)
ContextHolder.setOnRedirectTo(ssoRedirectInterceptor);

// Wrapper component that uses useAuth hook inside FronteggProvider context
function AppContent() {
  const { isLoading, isAuthenticated } = useAuth();
  const interceptSet = useRef(false);

  // Re-set our interceptor after FronteggProvider overwrites it
  useEffect(() => {
    if (!interceptSet.current) {
      ContextHolder.setOnRedirectTo(ssoRedirectInterceptor);
      interceptSet.current = true;
    }
  }, []);

  // If this app is configured for external login (local dev), redirect
  // authenticated users to the legacy app immediately before rendering any UI
  if (authService.useExternalLogin && isAuthenticated) {
    window.location.href = authService.authAppUrl;
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return <Outlet />;
}

function App() {
  return (
    <FronteggProvider
      authOptions={authOptions}
      contextOptions={contextOptions}
      themeOptions={themeOptions}
      hostedLoginBox={false}
      entitlementsOptions={{ enabled: true }}
    >
      <AppContent />
    </FronteggProvider>
  );
}

export default App;
